/* eslint-disable jsx-a11y/label-has-associated-control */
import Button from '@material-ui/core/Button';
import Popper, { PopperProps } from '@material-ui/core/Popper';
import GooglePlacesAutocomplete from '@orientaction/components/PlacesAutocomplete';
import MaterialPhoneInput from '@orientaction/components/MaterialPhoneInput';
import { isValidPhoneNumber } from 'libphonenumber-js';
import moment from 'moment';
import { Controller } from 'react-hook-form';
import { useProfileEditSharedStyles } from '../Edit.styles';
import { useRecrutementForm } from '../hooks/useRecrutementForm';
import locale from '../locale.json';
import { useRecrutementFormStyles } from './RecrutementForm.styles';
import { useRecrutementModalStyles } from './RecrutementModal.styles';

type RecrutementFormProps = {
  user: Record<string, any>;
  onClose: () => void;
  getUser?: () => void;
  idEbook?: string | number;
};

const GENDER_OPTIONS = [
  { value: 'homme', label: 'Homme' },
  { value: 'femme', label: 'Femme' },
  { value: 'Non binaire', label: 'Non binaire' },
];

const DIPLOMA_OPTIONS = [
  'Aucun diplôme',
  'CAP/BEP',
  'BAC',
  'BAC+1',
  'BAC+2',
  'BAC+3',
  'BAC+4',
  'BAC+5',
  'BAC+6',
  'BAC+7',
  'BAC+8',
];

const EXPERIENCE_OPTIONS = [
  { value: 'Débutant', label: 'Débutant' },
  { value: '1 an', label: '1 an' },
  { value: '2 ans', label: '2 ans' },
  { value: '3 ans', label: '3 ans' },
  { value: '4 ans', label: '4 ans' },
  { value: '5 ans', label: '5 ans' },
  { value: '6 ans', label: '6 ans' },
  { value: '7 ans', label: '7 ans' },
  { value: '8 ans', label: '8 ans' },
  { value: '9 ans', label: '9 ans' },
  { value: '10 ans', label: '10 ans' },
  { value: '11 ans', label: '+ de 10 ans' },
];

const POSTE_OPTIONS = [
  { value: 'en poste', label: 'En poste' },
  { value: 'fin de contrat', label: 'En poste, mais fin de contrat bientôt' },
  { value: 'sans emploi', label: 'Sans emploi' },
];

// RecrutementModal overlay is 1304. The places menu defaults to 1300, so it renders behind the dialog.
const RECRUITMENT_PLACES_POPPER_Z_INDEX = 1400;

const RecruitmentPlacesPopper = ({ style, ...props }: PopperProps) => (
  <Popper {...props} style={{ ...style, zIndex: RECRUITMENT_PLACES_POPPER_Z_INDEX }} />
);

const NOTICE_OPTIONS = [
  'dès que possible',
  '1 mois',
  '2 mois',
  '3 mois',
  '4 mois',
  '5 mois',
  '6 mois',
  '7 mois',
  '8 mois',
  '9 mois',
  '10 mois',
  '11 mois',
  '12 mois',
];

const RecrutementForm = ({ user, onClose, getUser, idEbook }: RecrutementFormProps) => {
  const local = useRecrutementFormStyles();
  const modalClasses = useRecrutementModalStyles();
  const shared = useProfileEditSharedStyles();
  const classes = { ...shared, ...local };
  const {
    control,
    errors,
    isValid,
    handleSubmit,
    onSubmitHandler,
    placeValue,
    setPlaceValue,
    onPlaceChange,
  } = useRecrutementForm({ user, onClose, getUser, idEbook });

  const renderError = (name: string) => {
    const message = errors?.[name]?.message;
    return message ? <span className={classes.errorField}>{String(message)}</span> : null;
  };

  const hasMissingRequiredField = !isValid || !placeValue;

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmitHandler)}>
      {hasMissingRequiredField && (
        <p className={modalClasses.modalCaption}>{locale.recrutementModalCaption}</p>
      )}
      <div className={classes.fieldRow}>
        <div className={classes.field}>
          <Controller
            name="gender"
            control={control}
            defaultValue={user?.gender || ''}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field }) => (
              <>
                <label className={classes.label} htmlFor="gender">
                  {locale.gender}
                </label>
                <select {...field} id="gender" className={classes.select} data-cy="form-gender">
                  <option value="">Sélectionner</option>
                  {GENDER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {renderError('gender')}
              </>
            )}
          />
        </div>

        <div className={classes.field}>
          <Controller
            name="dayOfBirth"
            control={control}
            defaultValue={user.dayOfBirth ? moment(user.dayOfBirth).format('YYYY-MM-DD') : ''}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field }) => (
              <>
                <label className={classes.label} htmlFor="dayOfBirth">
                  {locale.birthday}
                </label>
                <input {...field} id="dayOfBirth" type="date" className={classes.input} />
                {renderError('dayOfBirth')}
              </>
            )}
          />
        </div>
      </div>

      <div className={classes.field}>
        <Controller
          name="diploma"
          control={control}
          defaultValue={user?.diploma || ''}
          rules={{ required: 'Champ obligatoire' }}
          render={({ field }) => (
            <>
              <label className={classes.label} htmlFor="diploma">
                {locale.diploma}
              </label>
              <select {...field} id="diploma" className={classes.select} data-cy="form-diploma">
                <option value="">Sélectionner</option>
                {DIPLOMA_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {renderError('diploma')}
            </>
          )}
        />
      </div>

      <div className={classes.fieldRow}>
        <div className={classes.field}>
          <Controller
            name="streetNumber"
            control={control}
            defaultValue={user?.streetNumber}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field }) => (
              <>
                <label className={classes.label} htmlFor="streetNumber">
                  {locale.streetNumber}
                </label>
                <input
                  {...field}
                  id="streetNumber"
                  type="text"
                  className={classes.input}
                  placeholder="5"
                />
                {renderError('streetNumber')}
              </>
            )}
          />
        </div>

        <div className={classes.field}>
          <Controller
            name="streetName"
            control={control}
            defaultValue={user?.streetName}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field }) => (
              <>
                <label className={classes.label} htmlFor="streetName">
                  {locale.streetName}
                </label>
                <input {...field} id="streetName" type="text" className={classes.input} />
                {renderError('streetName')}
              </>
            )}
          />
        </div>
      </div>

      <div className={classes.fieldRow}>
        <div className={classes.field}>
          <Controller
            name="postal_code"
            control={control}
            defaultValue={user?.postal_code}
            rules={{ required: 'Champ obligatoire' }}
            render={({ field }) => (
              <>
                <label className={classes.label} htmlFor="postal_code">
                  {locale.postalCode}
                </label>
                <input
                  {...field}
                  id="postal_code"
                  type="text"
                  className={classes.input}
                  placeholder="78000"
                />
                {renderError('postal_code')}
              </>
            )}
          />
        </div>

        <div className={classes.field}>
          <label className={classes.label} htmlFor="town">
            {locale.city}
          </label>
          <div className={classes.placesField}>
            <GooglePlacesAutocomplete
              inputProps={{
                placeholder: 'Paris (75)',
                onKeyDown: e => {
                  if (e.key === 'Backspace') setPlaceValue('');
                },
                onClick: () => setPlaceValue(''),
              }}
              onDone={onPlaceChange}
              inputValue={placeValue || ''}
              setInputValue={setPlaceValue}
              label="Enter your location"
              apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
              PopperComponent={RecruitmentPlacesPopper}
            />
          </div>
          {renderError('town')}
        </div>
      </div>

      <div className={classes.field}>
        <Controller
          name="phone"
          control={control}
          defaultValue={user?.phone}
          rules={{
            required: 'Champ obligatoire',
            validate: {
              isValid: value =>
                isValidPhoneNumber(value || '') || 'Le format du numéro est invalide',
            },
            minLength: { value: 3, message: 'Le numéro est trop court' },
          }}
          render={({ field, fieldState }) => (
            <>
              <label className={classes.label} htmlFor="phone">
                {locale.phone}
              </label>
              <div className={classes.phoneField}>
                <MaterialPhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  onCountryChange={field.onBlur}
                  error={fieldState.invalid ? "Le numéro n'est pas au bon format" : false}
                />
              </div>
              {renderError('phone')}
            </>
          )}
        />
      </div>

      <div className={classes.field}>
        <Controller
          name="yearExperiences"
          control={control}
          defaultValue={user?.yearExperiences || ''}
          rules={{ required: 'Champ obligatoire' }}
          render={({ field }) => (
            <>
              <label className={classes.label} htmlFor="yearExperiences">
                {locale.yearExperiences}
              </label>
              <select {...field} id="yearExperiences" className={classes.select}>
                <option value="">Sélectionner</option>
                {EXPERIENCE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {renderError('yearExperiences')}
            </>
          )}
        />
      </div>

      <div className={classes.field}>
        <Controller
          name="poste"
          control={control}
          defaultValue={user?.poste || ''}
          rules={{ required: 'Champ obligatoire' }}
          render={({ field }) => (
            <>
              <label className={classes.label} htmlFor="poste">
                {locale.currentSituation}
              </label>
              <select {...field} id="poste" className={classes.select}>
                <option value="">Sélectionner</option>
                {POSTE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {renderError('poste')}
            </>
          )}
        />
      </div>

      <div className={classes.field}>
        <Controller
          name="noticeDeparture"
          control={control}
          defaultValue={user?.noticeDeparture || ''}
          rules={{ required: 'Champ obligatoire' }}
          render={({ field }) => (
            <>
              <label className={classes.label} htmlFor="noticeDeparture">
                {locale.noticeDeparture}
              </label>
              <select {...field} id="noticeDeparture" className={classes.select}>
                <option value="">Sélectionner</option>
                {NOTICE_OPTIONS.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {renderError('noticeDeparture')}
            </>
          )}
        />
      </div>

      <Button
        type="submit"
        className={classes.submitButton}
        disabled={!isValid || !placeValue}
        fullWidth={true}>
        {locale.recrutementConfirm}
      </Button>
    </form>
  );
};

export default RecrutementForm;
